import { useState, useEffect, useRef } from "react";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import Spinner from "../../ui/Spinner/Spinner";
import { Wallet, Transaction } from "../types";
import { ChevronDown, Phone, CheckCircle } from "lucide-react";
import { request } from "../../../utils";
import { MOBILE_LENGTH_BY_ISO2, SupportedISO2 } from "../../../utils/constant";

interface Provider {
  code: string;
  name: string;
  logo: string;
  hasGateway: boolean;
}

interface CurrencyData {
  code: string;
  iso2: string;
  symbol: string;
  name: string;
  currencyCode?: string;
  dialCode?: string;
}

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete: (transaction?: Transaction) => void;
  wallet: Wallet;
  amount: number;
  fee: number;
  providers: Provider[];
  currencyData: CurrencyData;
}

export const MobileMoneyModal = ({
  isOpen,
  onClose,
  onPaymentComplete,
  wallet,
  amount,
  fee,
  providers,
  currencyData,
}: Props) => {
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [showAuthorization, setShowAuthorization] = useState(false);
  const [countdown, setCountdown] = useState(120);
  // FIX: Use Transaction['status'] type which includes 'UNKNOWN'
  const [transactionStatus, setTransactionStatus] = useState<
    Transaction["status"] | null
  >(null);

  const [successTransaction, setSuccessTransaction] = useState<Transaction | null>(null);
  // Use a ref to track the latest transaction status for the interval callback
  const transactionStatusRef = useRef<Transaction["status"] | null>(null);

  const amountAfterFee = amount - fee;


  const dialCode = currencyData.dialCode || "233";
  const expectedPhoneLength = currencyData?.iso2 && currencyData.iso2 in MOBILE_LENGTH_BY_ISO2
    ? MOBILE_LENGTH_BY_ISO2[currencyData.iso2 as SupportedISO2] : 10;

  // Keep the ref updated with the latest transaction status
  useEffect(() => {
    transactionStatusRef.current = transactionStatus;
  }, [transactionStatus]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Countdown timer - using number type instead of NodeJS.Timeout
  useEffect(() => {
    let timer: number;

    if (showAuthorization && countdown > 0 && transactionId) {
      timer = window.setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Auto-check transaction status when timer expires
            checkTransactionStatus();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [showAuthorization, countdown, transactionId]);

  const resetForm = () => {
    setSelectedProvider(null);
    setPhoneNumber("");
    setIsProviderDropdownOpen(false);
    setSubmitting(false);
    setError(null);
    setTransactionId(null);
    setShowAuthorization(false);
    setCountdown(120);
    setTransactionStatus(null);
    transactionStatusRef.current = null;
  };

  const handleProviderSelect = (provider: Provider) => {
    setSelectedProvider(provider);
    setIsProviderDropdownOpen(false);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const digitsOnly = e.target.value.replace(/\D/g, "");

    if (digitsOnly.length > expectedPhoneLength) return;
    setPhoneNumber(digitsOnly);
  };

  const validateForm = () => {
    if (!selectedProvider) {
      setError("Please select a mobile network");
      return false;
    }

  if (phoneNumber.length !== expectedPhoneLength) {
    setError(`Phone number must be ${expectedPhoneLength} digits for ${currencyData.iso2}`);
    return false;
    }

    return true;
  };


  const handleInitiatePayment = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      const fullPhoneNumber = `+${dialCode}${phoneNumber}`;

      const idempotencyKey = `${wallet.id}-${currencyData.code}-${amount}-${fullPhoneNumber}-${selectedProvider!.code}-${Date.now()}`;

      const response = (await request.post({
        url: "/transaction/fund-wallet",
        data: {
          amount: amount,
          currency: currencyData.code || currencyData.currencyCode,
          iso2: currencyData.iso2,
          paymentMethod: "MOBILE_MONEY",
          phoneNumber: fullPhoneNumber,
          network: selectedProvider!.code,
        },
        headers: {
          idempotencykey: idempotencyKey,
        },
      })) as ApiResponse;

      // Type guard to check if response has the expected structure
      if (
        response?.success &&
        response.data &&
        typeof response.data === "object" &&
        "id" in response.data
      ) {
        const transaction = response.data as Transaction;
        setTransactionId(transaction.id);
        setShowAuthorization(true);
        pollTransactionStatus(transaction.id);
      } else {
        setError(response?.message || "Failed to initiate payment");
      }
    } catch (err: any) {
      const message = err?.message?.toLowerCase() ?? "";

      if (
        message.includes("invalid correspondent") ||
        message.includes("correspondent code") || message.includes("No active deposit flow configuration")
      ) {
        setError(
          `${selectedProvider?.name ?? "This provider"} is not available for funding. Please choose another network.`,
        );
      } else {
        setError(err?.message || "Failed to initiate payment");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pollTransactionStatus = async (id: string) => {
    const poll = async (): Promise<boolean> => {
      try {
        // Pass as object with url property
        const response = (await request.get({
          url: `/transaction/${id}`,
        })) as ApiResponse;

        if (
          response?.success &&
          response.data &&
          typeof response.data === "object"
        ) {
          const transaction = response.data as Transaction;
          const status = transaction.status;

          // Set the status directly - now it matches the state type
          setTransactionStatus(status);

          if (status === "SUCCESSFUL") {
            setSuccessTransaction(transaction)
            setTimeout(() => {
              onPaymentComplete(transaction);
            }, 2000);
            return true;
          } else if (status === "FAILED") {
            setError("Payment failed. Please try again.");
            return true;
          }
          return false;
        }
        return false;
      } catch (error) {
        console.error("Error polling transaction:", error);
        return false;
      }
    };

    // Poll every 5 seconds for up to 2 minutes
    const interval = window.setInterval(async () => {
      const isComplete = await poll();
      if (isComplete || countdown <= 0) {
        clearInterval(interval);
      }
    }, 5000);

    // Stop polling after 2 minutes
    setTimeout(() => {
      clearInterval(interval);
      // Use the ref to get the latest status
      const currentStatus = transactionStatusRef.current;
      if (
        currentStatus === null ||
        currentStatus === "PENDING" ||
        currentStatus === "UNKNOWN"
      ) {
        checkTransactionStatus();
      }
    }, 120000);
  };

  const checkTransactionStatus = async () => {
    if (!transactionId) return;

    try {
      // Pass as object with url property
      const response = (await request.get({
        url: `/transaction/${transactionId}`,
      })) as ApiResponse;

      if (
        response?.success &&
        response.data &&
        typeof response.data === "object"
      ) {
        const transaction = response.data as Transaction;
        const status = transaction.status;

        // Set the status directly - now it matches the state type
        setTransactionStatus(status);

        if (status === "SUCCESSFUL") {
        setSuccessTransaction(transaction)
          setTimeout(() => {
            onPaymentComplete(transaction);
          }, 2000);
        } else if (status === "FAILED") {
          setError("Payment failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error checking transaction status:", error);
    }
  };

  const handleAuthorizePayment = async () => {
    if (!transactionId) return;

    try {
      setSubmitting(true);
      await checkTransactionStatus();
    } finally {
      setSubmitting(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getCountdownColor = () => {
    if (countdown > 60) return "text-green-500";
    if (countdown > 30) return "text-yellow-500";
    return "text-red-500";
  };

  // Render success/status modals
  if (transactionStatus === "SUCCESSFUL") {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
            Payment Completed
          </h2>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            Your fund request has been successfully and securely processed and
            your {wallet.name.toLowerCase()} wallet has been funded with{" "}
            {wallet.symbol} {amountAfterFee.toFixed(2)}.
          </p>
          <button
            onClick={() => onPaymentComplete(successTransaction ?? undefined)}
            className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  if (showAuthorization) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
      >
        <div className="text-center">
          <div className="mb-6">
            <div className={`text-4xl font-bold ${getCountdownColor()} mb-2`}>
              {formatCountdown(countdown)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Time remaining to authorize
            </div>
          </div>

          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-center mb-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {wallet.symbol} {amountAfterFee.toFixed(2)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Amount to receive
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Phone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
              Check Your Phone
            </h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Please check your phone for a prompt and enter your mobile money
              PIN to authorize this transaction
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Transaction ID: {transactionId?.substring(0, 8)}...
            </div>
          </div>

          {error && (
            <div className="mb-4">
              <Alert variant="error" title="Error" message={error} />
            </div>
          )}

          {transactionStatus === "FAILED" && (
            <div className="mb-4">
              <Alert
                variant="error"
                title="Payment Failed"
                message="The payment authorization failed. Please try again."
              />
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={handleAuthorizePayment}
              disabled={submitting}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner className="h-4 w-4" />
                  Checking...
                </span>
              ) : transactionStatus === "UNKNOWN" ? (
                "Processing..."
              ) : transactionStatus === "FAILED" ? (
                "Payment Failed"
              ) : (
                "I've Authorized the Payment"
              )}
            </button>

            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
    >
      <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
        Mobile Money Payment
      </h2>

      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        Complete your {wallet.symbol} {amount.toFixed(2)} deposit
      </p>

      {error && (
        <div className="mb-4">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      {/* Amount Summary */}
      <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-2 text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {wallet.symbol}{" "}
            {amount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>
      </div>

      {/* Network Selection */}
      <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Network Provider
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsProviderDropdownOpen(!isProviderDropdownOpen)}
            className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
          >
            <div className="flex items-center">
              {selectedProvider ? (
                <>
                  {selectedProvider.logo && (
                    <img
                      src={selectedProvider.logo}
                      alt={selectedProvider.name}
                      className="mr-3 h-5 w-5"
                    />
                  )}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedProvider.name}
                  </span>
                </>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">
                  Select a network
                </span>
              )}
            </div>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform ${
                isProviderDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isProviderDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
              <div className="max-h-60 overflow-auto py-1">
                {providers.map((provider) => (
                  <button
                    key={provider.code}
                    type="button"
                    onClick={() => handleProviderSelect(provider)}
                    className={`flex w-full items-center px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      selectedProvider?.code === provider.code
                        ? "bg-gray-100 dark:bg-gray-700"
                        : ""
                    }`}
                  >
                    {provider.logo && (
                      <img
                        src={provider.logo}
                        alt={provider.name}
                        className="mr-3 h-5 w-5"
                      />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white">
                      {provider.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Phone Number Input */}
      <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Phone Number
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500 dark:text-gray-400">+{dialCode}</span>
          </div>
          <input
            type="tel"
            value={
             phoneNumber
            }
            onChange={handlePhoneChange}
            placeholder="Enter Mobile Money number"
            className="w-full rounded-lg border border-gray-200 bg-white pl-16 pr-4 py-3 text-gray-900 transition-colors focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-primary/50"
            maxLength={expectedPhoneLength}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {`Enter a ${expectedPhoneLength}-digit mobile money number (without +${dialCode}).`}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <button
          onClick={handleInitiatePayment}
          disabled={submitting || !selectedProvider || !phoneNumber}
          className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner className="h-4 w-4" />
              Processing...
            </span>
          ) : (
            "Continue"
          )}
        </button>

        <button
          onClick={onClose}
          className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};
